import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { logger } from '../../lib/logger';

export function EmailConfirmation() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Confirmando tu correo electrónico...');
  const navigate = useNavigate();

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        // Get the hash from URL (Supabase sends #access_token=... or #error=...)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const error = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');

        if (error) {
          setStatus('error');
          setMessage(errorDescription || 'Error al confirmar el correo electrónico.');
          return;
        }

        if (!accessToken) {
          setStatus('error');
          setMessage('No se encontró el token de confirmación.');
          return;
        }

        // Email is automatically confirmed by Supabase when user clicks the link
        // Just verify the session is valid
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          setStatus('error');
          setMessage('Sesión inválida. Por favor intenta de nuevo.');
          return;
        }

        setStatus('success');
        setMessage('¡Tu correo ha sido confirmado exitosamente!');

        // Redirect to app after 2 seconds
        setTimeout(() => {
          navigate('/organization-setup');
        }, 2000);
      } catch (err) {
        logger.error('[EmailConfirmation] Error:', err);
        setStatus('error');
        setMessage('Ocurrió un error al confirmar tu correo.');
      }
    };

    handleEmailConfirmation();
  }, [navigate]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl text-center"
    >
      {status === 'loading' && (
        <>
          <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader2 size={40} className="text-blue-600 animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-[#111827] mb-3">
            Confirmando correo
          </h2>
          <p className="text-[#6B7280]">{message}</p>
        </>
      )}

      {status === 'success' && (
        <>
          <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={40} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#111827] mb-3">
            ¡Correo confirmado!
          </h2>
          <p className="text-[#6B7280] mb-6">{message}</p>
          <p className="text-sm text-[#6B7280]">
            Redirigiendo a la configuración...
          </p>
        </>
      )}

      {status === 'error' && (
        <>
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle size={40} className="text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#111827] mb-3">
            Error de confirmación
          </h2>
          <p className="text-[#6B7280] mb-6">{message}</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-[#5D1828] text-white rounded-lg font-medium hover:bg-[#7A2235] transition-colors shadow-lg shadow-[#5D1828]/30"
          >
            Volver al inicio de sesión
          </Link>
        </>
      )}
    </motion.div>
  );
}
